import React, { useContext, useEffect, useMemo, useState } from 'react'
import { ChangePresentContext, MedPlanContext } from '../../SingleEvent'
import BigModal from '../../../../../components/Modals/BigModal'
import Send from '@mui/icons-material/Send'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { Delete, Edit } from '@mui/icons-material'
import {
  Alert,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  Paper,
  InputAdornment,
} from '@mui/material'

const OPEL_LEVELS = ['OPEL 1', 'OPEL 2', 'OPEL 3', 'OPEL 4']

const STANDARD_OPELS = [
  {
    level: 'OPEL 1',
    indicators: ['Expected event activity with planned resources sufficient.'],
    actions: ['Continue routine monitoring and maintain normal reporting cadence.'],
  },
  {
    level: 'OPEL 2',
    indicators: [
      'Activity or queueing increasing, with resources under moderate pressure.',
    ],
    actions: [
      'Clinical lead reviews demand, redeploys available resources and briefs event control.',
    ],
  },
  {
    level: 'OPEL 3',
    indicators: [
      'Sustained high demand, delayed response capability or limited treatment capacity.',
    ],
    actions: [
      'Escalate to event control, request additional support and prioritise resources by clinical risk.',
    ],
  },
  {
    level: 'OPEL 4',
    indicators: [
      'Severe operational pressure with resources unable to meet immediate clinical demand.',
    ],
    actions: [
      'Declare critical escalation, activate major incident or overwhelm plan as appropriate and seek external support.',
    ],
  },
]

const OPEL = () => {
  const { medPlan, setMedPlan } = useContext(MedPlanContext)
  const { changesPresent, setChangesPresent } = useContext(ChangePresentContext)

  const opels = useMemo(() => medPlan?.opels ?? [], [medPlan?.opels])

  const [openAddNew, setOpenAddNew] = useState(false)
  const [addNewLevel, setAddNewLevel] = useState('')
  const [addNewIndicators, setAddNewIndicators] = useState([])
  const [addNewActions, setAddNewActions] = useState([])
  const [newIndicatorText, setNewIndicatorText] = useState('')
  const [newActionText, setNewActionText] = useState('')
  const [selectedOPEL, setSelectedOPEL] = useState(null)

  useEffect(() => {
    // reset form when closing AND not editing
    if (!openAddNew && !selectedOPEL) {
      setAddNewLevel('')
      setAddNewActions([])
      setAddNewIndicators([])
      setNewIndicatorText('')
      setNewActionText('')
      return
    }

    // populate when editing
    if (selectedOPEL) {
      setOpenAddNew(true)
      setAddNewLevel(selectedOPEL.opel.level ?? '')
      setAddNewIndicators(selectedOPEL.opel.indicators ?? [])
      setAddNewActions(selectedOPEL.opel.actions ?? [])
    }
  }, [openAddNew, selectedOPEL])

  const canSubmit =
    !!addNewLevel && addNewIndicators.length > 0 && addNewActions.length > 0

  const markChanged = () => {
    if (!changesPresent) setChangesPresent(true)
  }

  const onCloseModal = () => {
    setOpenAddNew(false)
    setSelectedOPEL(null)
  }

  const submit = () => {
    setMedPlan((prev) => ({
      ...prev,
      opels: selectedOPEL
        ? (prev.opels ?? []).map((x, i) =>
            selectedOPEL.index === i
              ? {
                  ...x,
                  level: addNewLevel,
                  indicators: addNewIndicators,
                  actions: addNewActions,
                }
              : x,
          )
        : [
            ...(prev?.opels ?? []),
            {
              level: addNewLevel,
              indicators: addNewIndicators,
              actions: addNewActions,
            },
          ],
    }))

    markChanged()
    onCloseModal()
  }

  const addStandardOPELs = () => {
    setMedPlan((prev) => ({
      ...prev,
      opels: STANDARD_OPELS,
    }))
    markChanged()
  }

  const removeOPEL = (index) => {
    setMedPlan((prev) => ({
      ...prev,
      opels: (prev.opels ?? []).filter((_, i) => i !== index),
    }))
    markChanged()
  }

  return (
    <Grid item xs={12} container>
      <Stack width="100%" gap={2}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="baseline"
        >
          <Stack>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Operational Pressures Escalation Levels
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Define escalation triggers and actions for the event.
            </Typography>
          </Stack>

          <Button
            variant="contained"
            size="small"
            color="anBlue"
            onClick={() => setOpenAddNew(true)}
          >
            Add OPEL
          </Button>
        </Stack>

        <Stack gap={1.5}>
          {opels.length === 0 ? (
            <Paper
              variant="outlined"
              sx={{ p: 2, borderStyle: 'dashed', bgcolor: 'background.paper' }}
            >
              <Stack gap={1.25}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  No escalation levels added yet
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Start with the standard OPEL 1-4 structure, then adjust the
                  indicators and actions for this event.
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
                  <Button
                    variant="contained"
                    color="anBlue"
                    size="small"
                    onClick={addStandardOPELs}
                  >
                    Add Standard OPELs
                  </Button>
                  <Button
                    variant="outlined"
                    color="anBlue"
                    size="small"
                    onClick={() => setOpenAddNew(true)}
                  >
                    Add Custom Level
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          ) : (
            opels.map((opel, index) => (
              <Card key={index} variant="outlined" sx={{ borderRadius: 1 }}>
                <CardContent>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    gap={2}
                  >
                    <Stack gap={0.5}>
                      <Stack direction="row" alignItems="center" gap={1}>
                        <Chip
                          label={opel.level}
                          size="small"
                          sx={{ fontWeight: 700 }}
                        />
                        <Typography variant="subtitle2" color="text.secondary">
                          OPEL Level
                        </Typography>
                      </Stack>
                    </Stack>

                    <Stack direction="row" gap={0.5}>
                      <Tooltip title="Edit">
                        <IconButton
                          onClick={() => setSelectedOPEL({ index, opel })}
                          size="small"
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          onClick={() => removeOPEL(index)}
                          size="small"
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Stack>

                  <Divider sx={{ my: 1.5 }} />

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 700, mb: 1 }}
                      >
                        Indicators
                      </Typography>
                      <Stack direction="row" flexWrap="wrap" gap={1}>
                        {(opel.indicators ?? []).map((v, i) => (
                          <Paper
                            key={i}
                            variant="outlined"
                            sx={{
                              p: 1.5,
                              borderRadius: 2,
                              display: 'flex',
                              alignItems: 'flex-start',
                              justifyContent: 'space-between',
                              gap: 1,
                              backgroundColor: 'rgba(0,0,0,0.02)',
                              width: '100%',
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ whiteSpace: 'pre-wrap', flex: 1 }}
                            >
                              {v}
                            </Typography>
                          </Paper>
                        ))}
                      </Stack>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 700, mb: 1 }}
                      >
                        Actions
                      </Typography>
                      <Stack direction="row" flexWrap="wrap" gap={1}>
                        {(opel.actions ?? []).map((v, i) => (
                          <Paper
                            key={i}
                            variant="outlined"
                            sx={{
                              p: 1.5,
                              borderRadius: 2,
                              display: 'flex',
                              alignItems: 'flex-start',
                              justifyContent: 'space-between',
                              gap: 1,
                              backgroundColor: 'rgba(0,0,0,0.02)',
                              width: '100%',
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ whiteSpace: 'pre-wrap', flex: 1 }}
                            >
                              {v}
                            </Typography>
                          </Paper>
                        ))}
                      </Stack>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            ))
          )}
        </Stack>
      </Stack>

      <BigModal open={openAddNew} onClose={onCloseModal}>
        <Stack gap={2}>
          <Stack>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              {selectedOPEL ? 'Edit OPEL' : 'Add OPEL'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Add indicators and actions.
            </Typography>
          </Stack>

          <TextField
            value={addNewLevel}
            onChange={(e) => setAddNewLevel(e.target.value)}
            label="Level"
            autoComplete="off"
            placeholder="e.g. OPEL 2"
          />
          <Stack direction="row" flexWrap="wrap" gap={1}>
            {OPEL_LEVELS.map((level) => (
              <Chip
                key={level}
                label={level}
                color={addNewLevel === level ? 'primary' : 'default'}
                onClick={() => setAddNewLevel(level)}
                size="small"
              />
            ))}
          </Stack>

          {!selectedOPEL && (
            <Alert severity="info">
              For a full starter set, use <b>Add Standard OPELs</b> from the
              empty state. This dialog is best for custom additions or edits.
            </Alert>
          )}

          <Divider />

          {/* Indicators */}
          <Stack gap={1}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
              Indicators
            </Typography>

            <Stack gap={1}>
              {addNewIndicators.map((indicator, index) => (
                <Paper
                  key={index}
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 1,
                    backgroundColor: 'rgba(0,0,0,0.02)',
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: 'pre-wrap', flex: 1 }}
                  >
                    {indicator}
                  </Typography>

                  <IconButton
                    size="small"
                    onClick={() =>
                      setAddNewIndicators((prev) =>
                        prev.filter((_, i) => i !== index),
                      )
                    }
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Paper>
              ))}
            </Stack>

            <TextField
              value={newIndicatorText}
              onChange={(e) => setNewIndicatorText(e.target.value)}
              placeholder="Add indicator…"
              autoComplete="off"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      edge="end"
                      onClick={() => {
                        const t = newIndicatorText.trim()
                        if (!t) return
                        setAddNewIndicators((prev) => [...prev, t])
                        setNewIndicatorText('')
                      }}
                    >
                      <Send fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Stack>

          <Divider />

          {/* Actions */}
          <Stack gap={1}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
              Actions
            </Typography>

            <Stack gap={1}>
              {addNewActions.map((action, index) => (
                <Paper
                  key={index}
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 1,
                    backgroundColor: 'rgba(0,0,0,0.02)',
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: 'pre-wrap', flex: 1 }}
                  >
                    {action}
                  </Typography>

                  <IconButton
                    size="small"
                    onClick={() =>
                      setAddNewActions((prev) =>
                        prev.filter((_, i) => i !== index),
                      )
                    }
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Paper>
              ))}
            </Stack>

            <TextField
              value={newActionText}
              onChange={(e) => setNewActionText(e.target.value)}
              placeholder="Add action…"
              autoComplete="off"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      edge="end"
                      onClick={() => {
                        const t = newActionText.trim()
                        if (!t) return
                        setAddNewActions((prev) => [...prev, t])
                        setNewActionText('')
                      }}
                    >
                      <Send fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Stack>

          <Stack
            direction="row"
            justifyContent="flex-end"
            gap={1}
            sx={{ pt: 1 }}
          >
            <Button variant="text" onClick={onCloseModal}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="success"
              onClick={submit}
              disabled={!canSubmit}
            >
              Save
            </Button>
          </Stack>
        </Stack>
      </BigModal>
    </Grid>
  )
}

export default React.memo(OPEL)
